import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { marketplaceApi, toApiChannel } from "./marketplaceApi";
import type { ConnectionSettings } from "./marketplaceTypes";

function useTenant() {
  return useAuthStore((s) => s.currentTenant);
}

export const marketplaceKeys = {
  all: (tenant: string | null) => ["marketplace", tenant] as const,
  connections: (tenant: string | null) =>
    ["marketplace", "connections", tenant] as const,
  syncStatus: (tenant: string | null, channel: string, connectionName?: string) =>
    ["marketplace", "sync-status", tenant, channel, connectionName] as const,
  syncLog: (tenant: string | null, channel: string, page: number, connectionName?: string) =>
    ["marketplace", "sync-log", tenant, channel, page, connectionName] as const,
  channelSummary: (tenant: string | null, channel: string, connectionName?: string, period?: string) =>
    ["marketplace", "summary", tenant, channel, connectionName, period] as const,
};

export function useMarketplaceConnections() {
  const tenant = useTenant();
  return useQuery({
    queryKey: marketplaceKeys.connections(tenant),
    queryFn: () => marketplaceApi.getConnections(),
    enabled: !!tenant,
  });
}

/** Fetch sync status for a specific connection */
export function useConnectionStatus(channel: string, connectionName: string) {
  const tenant = useTenant();
  const apiChannel = toApiChannel(channel);
  return useQuery({
    queryKey: marketplaceKeys.syncStatus(tenant, apiChannel, connectionName),
    queryFn: () => marketplaceApi.getSyncStatus(apiChannel, connectionName),
    enabled: !!tenant && !!connectionName,
    refetchInterval: (query) => {
      return query.state.data?.syncing ? 5000 : false;
    },
  });
}

/** @deprecated Use useConnectionStatus for per-connection status */
export function useMarketplaceStatus(channel: string) {
  const tenant = useTenant();
  const apiChannel = toApiChannel(channel);
  return useQuery({
    queryKey: marketplaceKeys.syncStatus(tenant, apiChannel),
    queryFn: () => marketplaceApi.getSyncStatus(apiChannel),
    enabled: !!tenant,
    refetchInterval: (query) => {
      return query.state.data?.syncing ? 5000 : false;
    },
  });
}

/** Fetch sync log for a specific connection */
export function useConnectionSyncLog(channel: string, connectionName: string, page = 1) {
  const tenant = useTenant();
  const apiChannel = toApiChannel(channel);
  return useQuery({
    queryKey: marketplaceKeys.syncLog(tenant, apiChannel, page, connectionName),
    queryFn: () => marketplaceApi.getSyncLog(apiChannel, page, undefined, connectionName),
    enabled: !!tenant && !!connectionName,
    placeholderData: keepPreviousData,
  });
}

/** @deprecated Use useConnectionSyncLog for per-connection logs */
export function useSyncLog(channel: string, page = 1) {
  const tenant = useTenant();
  const apiChannel = toApiChannel(channel);
  return useQuery({
    queryKey: marketplaceKeys.syncLog(tenant, apiChannel, page),
    queryFn: () => marketplaceApi.getSyncLog(apiChannel, page),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function useChannelSummary(channel: string, enabled = true, period?: string) {
  const tenant = useTenant();
  const apiChannel = toApiChannel(channel);
  return useQuery({
    queryKey: marketplaceKeys.channelSummary(tenant, apiChannel, undefined, period),
    queryFn: () => marketplaceApi.getChannelSummary(apiChannel, undefined, period),
    enabled: !!tenant && enabled,
  });
}

export function useTriggerSync() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({
      channel,
      syncType,
      connectionName,
    }: {
      channel: string;
      syncType: string;
      connectionName?: string;
    }) => marketplaceApi.triggerSync(channel, syncType, connectionName),
    onSuccess: (_data, variables) => {
      toast.success("เริ่มซิงค์แล้ว");
      // Invalidate all sync-status keys for this channel (covers all connections)
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "sync-status", tenant, variables.channel],
      });
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "sync-log", tenant, variables.channel],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ไม่สามารถเริ่มซิงค์ได้");
    },
  });
}

export function useTriggerSyncAll() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({ channel }: { channel: string }) =>
      marketplaceApi.triggerSyncAll(channel),
    onSuccess: (_data, variables) => {
      toast.success("เริ่มซิงค์ทุกบัญชีแล้ว");
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "sync-status", tenant, variables.channel],
      });
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "sync-log", tenant, variables.channel],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ไม่สามารถเริ่มซิงค์ได้");
    },
  });
}

export function usePushAllChannel() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({ channel, connectionName }: { channel: string; connectionName: string }) =>
      marketplaceApi.pushAllChannel(channel, connectionName),
    onSuccess: (_data, variables) => {
      toast.success("เริ่มพุชทั้งหมดแล้ว");
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "sync-status", tenant, variables.channel],
      });
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "sync-log", tenant, variables.channel],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ไม่สามารถเริ่มพุชได้");
    },
  });
}

export function useUpdateConnectionSettings() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({ connectionName, settings }: { connectionName: string; settings: ConnectionSettings }) =>
      marketplaceApi.updateConnectionSettings(connectionName, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(tenant),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ไม่สามารถอัพเดทการตั้งค่าได้");
    },
  });
}

export function useDisconnectChannel() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({ channel, connectionName }: { channel: string; connectionName?: string }) =>
      marketplaceApi.disconnect(channel, connectionName),
    onSuccess: () => {
      toast.success("ยกเลิกการเชื่อมต่อแล้ว");
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.all(tenant),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ไม่สามารถยกเลิกการเชื่อมต่อได้");
    },
  });
}
